/**
 * Timer Debug Panel
 * Shows turn timer countdown and pause/resume controls.
 * Only visible in dev mode for admins.
 *
 * Uses scheduledJobs table for accurate timer display,
 * Convex realtime subscriptions for schedule updates,
 * and client-side calculations for smooth countdown.
 */

import { View, Text, StyleSheet, Pressable, TouchableOpacity } from 'react-native'
import { useMemo, useState } from 'react'
import { useMutation, useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import type { ScheduledJob } from '@ppwr/convex/shared/validators/scheduledJobs'
import { Palette } from '@ppwr/poker'
import { useAnimationFrame } from './hooks/useAnimationFrame'
import { Draggable } from './Draggable/Draggable'
import { useGameQuery } from '../../hooks/convex/useGameState'
import { useGamePanelsStore } from '../../screens/GameContainer/useGamePanelsStore'

const CREAM = Palette['Brand/Cream']
const BLACK = Palette['Global/Black']

interface TimerDebugPanelProps {
  game: ReturnType<typeof useGameQuery>['game']
  gameId: Id<'games'>
}

type ScheduleTypeInfo = Record<ScheduledJob['scheduleType'], { label: string; emoji: string }>

const scheduleTypeInfo: ScheduleTypeInfo = {
  turnTimer: { label: 'Turn Timer', emoji: '⏱️' },
  botAction: { label: 'Bot Action', emoji: '🤖' },
  roundProgression: { label: 'Round Progress', emoji: '🎲' },
  showdownReveal: { label: 'Showdown Reveal', emoji: '🃏' },
  winnersReveal: { label: 'Winners Reveal', emoji: '🎉' },
  showdownDuration: { label: 'Showdown Duration', emoji: '🃏' }, // TODO: Remove after migration
  botFillStep: { label: 'Bot Fill Step', emoji: '👤' }
}

export function TimerDebugPanel({ game, gameId }: TimerDebugPanelProps) {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const { setTimingPanel } = useGamePanelsStore()

  const pauseGame = useMutation(api.games.mutations.pauseGame)
  const resumeGame = useMutation(api.games.mutations.resumeGame)

  // Get players to show current player info
  const players = useQuery(api.players.queries.getPlayers, { gameId })

  // Get all pending schedules for this game
  const schedules = useQuery(api.games.queries.getGameSchedules, { gameId })

  // Server state (from Convex subscription)
  const isPaused = game?.state === 'playing' ? Boolean(game.pausedAt) : false
  const pausedAt = game?.state === 'playing' ? game.pausedAt : null
  const currentPlayerId = game?.state === 'playing' ? game.currentPlayerId : null
  const turnDuration = game?.state === 'playing' ? game.turnDuration : 60000
  const turnStartedAt = game?.state === 'playing' ? game.turnStartedAt : null

  // Current player info
  const currentPlayer = players?.find((p) => p._id === currentPlayerId)
  const playerType = currentPlayer?.kind ?? '?'

  // Find active schedule for current player (prioritize turnTimer over botAction)
  // Also check for round progression, showdown reveal, winners reveal (no playerId requirement)
  const activeSchedule = useMemo(() => {
    if (!schedules) return null

    // First, try to find player-specific schedule (turnTimer or botAction)
    if (currentPlayerId) {
      const playerSchedule = schedules.find(
        (s) => (s.scheduleType === 'turnTimer' || s.scheduleType === 'botAction') && s.playerId === currentPlayerId
      )
      if (playerSchedule) return playerSchedule
    }

    // If no player schedule, check for 'roundProgression' and 'showdownReveal' (game-level)
    const roundSchedule = schedules.find((s) => s.scheduleType === 'roundProgression')
    if (roundSchedule) return roundSchedule

    // Showdown phase: for X-sec players see cards
    const showdownRevealSchedule = schedules.find((s) => s.scheduleType === 'showdownReveal')
    if (showdownRevealSchedule) return showdownRevealSchedule

    // Winners phase: hand complete, see pots, waiting for next hand
    const winnersRevealSchedule = schedules.find((s) => s.scheduleType === 'winnersReveal')
    if (winnersRevealSchedule) return winnersRevealSchedule

    return null
  }, [schedules, currentPlayerId])

  // Use animation frame for smooth countdown (only when not paused)
  const now = useAnimationFrame(!isPaused)

  // Calculate timer from actual schedule data OR paused state
  const timerData = useMemo(() => {
    // PAUSED STATE: Show frozen elapsed/remaining breakdown
    if (isPaused && pausedAt && turnStartedAt) {
      const elapsed = pausedAt - turnStartedAt
      const remaining = turnDuration - elapsed

      return {
        isPaused: true,
        remaining: Math.max(0, remaining),
        elapsed: Math.max(0, elapsed),
        // Progress bar shows remaining time (1.0 = full, 0.0 = empty)
        progress: Math.max(0, Math.min(1, remaining / turnDuration)),
        scheduleType: playerType === 'bot' ? ('botAction' as const) : ('turnTimer' as const),
        scheduledFor: pausedAt + remaining,
        totalDuration: turnDuration,
        // Bot dual timer fields (inferred from game state during pause)
        actualBotDelay: undefined,
        uiDisplayDuration: turnDuration,
        uiDisplayElapsed: elapsed
      }
    }

    // ACTIVE STATE: Use real schedule data
    if (!activeSchedule) return null

    // Calculate remaining time from scheduledFor (accurate!)
    const remaining = activeSchedule.scheduledFor - now

    // Try to calculate elapsed from schedule metadata or game state
    let elapsed = 0
    let totalDuration = turnDuration

    // For botAction schedules with UI display info
    const botDelayMs =
      activeSchedule.scheduleType === 'botAction' && activeSchedule.scheduledAt
        ? activeSchedule.scheduledFor - activeSchedule.scheduledAt
        : undefined

    // Use turnStartedAt for more accurate elapsed calculation
    if (turnStartedAt) {
      elapsed = now - turnStartedAt
    } else if (activeSchedule.scheduledAt) {
      elapsed = now - activeSchedule.scheduledAt
      totalDuration = activeSchedule.scheduledFor - activeSchedule.scheduledAt
    }

    // Progress bar: remaining / total (1.0 = full, 0.0 = empty)
    const progress = Math.max(0, Math.min(1, remaining / totalDuration))

    return {
      isPaused: false,
      remaining: Math.max(0, remaining),
      elapsed: Math.max(0, elapsed),
      progress,
      scheduleType: activeSchedule.scheduleType,
      scheduledFor: activeSchedule.scheduledFor,
      totalDuration,
      // Bot dual timer: actual bot delay vs UI display
      actualBotDelay: botDelayMs,
      uiDisplayDuration: turnDuration,
      uiDisplayElapsed: turnStartedAt ? now - turnStartedAt : elapsed
    }
  }, [activeSchedule, now, isPaused, pausedAt, turnStartedAt, turnDuration, playerType])

  // If no timer data, show minimal "waiting" state
  if (!timerData) {
    return (
      <Draggable minWidth={240} showHandle={true} containerStyle={styles.draggableContainer}>
        <View style={styles.container}>
          <View style={styles.header}>
            <Text style={styles.title}>Turn Timer</Text>
            <View style={styles.headerRight}>
              <View style={[styles.statusDot, styles.waitingDot]} />
              <TouchableOpacity hitSlop={12} onPress={() => setIsCollapsed(!isCollapsed)}>
                <Text style={styles.collapseIcon}>{isCollapsed ? '▼' : '▲'}</Text>
              </TouchableOpacity>
              <TouchableOpacity hitSlop={12} onPress={() => setTimingPanel(false)}>
                <Text style={styles.closeIcon}>✕</Text>
              </TouchableOpacity>
            </View>
          </View>

          {!isCollapsed && (
            <View style={styles.content}>
              <Text style={styles.waitingText}>Waiting for schedule...</Text>

              {/* Pause/Resume Button */}
              <Pressable
                onPress={() => {
                  isPaused ? resumeGame({ gameId }) : pauseGame({ gameId })
                }}
                style={({ pressed }) => [styles.button, isPaused && styles.activeButton, pressed && { opacity: 0.7 }]}
              >
                <Text style={[styles.buttonText, isPaused && styles.activeButtonText]}>
                  {isPaused ? 'Resume' : 'Pause'}
                </Text>
              </Pressable>
            </View>
          )}
        </View>
      </Draggable>
    )
  }

  const remainingSeconds = Math.ceil(timerData.remaining / 1000)
  const totalSeconds = Math.ceil(timerData.totalDuration / 1000)

  // Visual feedback based on remaining time
  const isUrgent = remainingSeconds <= 10 && remainingSeconds > 5
  const isCritical = remainingSeconds <= 5

  // Progress bar color
  const getProgressColor = () => {
    if (isPaused) return '#9E9E9E'
    if (isCritical) return '#EF5350'
    if (isUrgent) return '#FFA726'
    return '#4CAF50'
  }

  // Schedule Info
  const scheduleTypeEmoji = scheduleTypeInfo[timerData.scheduleType]?.emoji || '❓'
  const scheduleTypeLabel = scheduleTypeInfo[timerData.scheduleType]?.label || 'Unknown Timer'

  // Bot dual timer calculations
  const showBotDualTimer = timerData.scheduleType === 'botAction' && timerData.actualBotDelay !== undefined
  const botActualRemaining = showBotDualTimer ? timerData.remaining : 0
  const botUiRemaining =
    showBotDualTimer && timerData.uiDisplayDuration && timerData.uiDisplayElapsed
      ? timerData.uiDisplayDuration - timerData.uiDisplayElapsed
      : 0

  return (
    <Draggable minWidth={260} showHandle={true} containerStyle={styles.draggableContainer}>
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>
            {scheduleTypeEmoji} {scheduleTypeLabel}
          </Text>
          <View style={styles.headerRight}>
            <View style={[styles.statusDot, isPaused ? styles.pausedDot : styles.activeDot]} />
            <TouchableOpacity hitSlop={12} onPress={() => setIsCollapsed(!isCollapsed)}>
              <Text style={styles.collapseIcon}>{isCollapsed ? '▼' : '▲'}</Text>
            </TouchableOpacity>
            <TouchableOpacity hitSlop={12} onPress={() => setTimingPanel(false)}>
              <Text style={styles.closeIcon}>✕</Text>
            </TouchableOpacity>
          </View>
        </View>

        {!isCollapsed && (
          <View style={styles.content}>
            {/* Bot Dual Timer Display */}
            {showBotDualTimer ? (
              <View style={styles.dualTimerContainer}>
                {/* Actual Bot Timer */}
                <View style={styles.timerColumn}>
                  <Text style={styles.timerLabel}>Actual</Text>
                  <Text style={[styles.timer, styles.timerSmall, isPaused && styles.timerPaused]}>
                    {Math.ceil(botActualRemaining / 1000)}s
                  </Text>
                </View>

                {/* UI Display Timer */}
                <View style={styles.timerColumn}>
                  <Text style={styles.timerLabel}>UI Display</Text>
                  <Text style={[styles.timer, styles.timerSmall, isPaused && styles.timerPaused]}>
                    {Math.ceil(botUiRemaining / 1000)}s
                  </Text>
                </View>
              </View>
            ) : (
              <>
                {/* Single Timer Display (for humans, roundProgression, or paused) */}
                <Text
                  style={[
                    styles.timer,
                    isCritical && styles.timerCritical,
                    isUrgent && !isCritical && styles.timerUrgent,
                    isPaused && styles.timerPaused
                  ]}
                >
                  {remainingSeconds}s
                </Text>
              </>
            )}

            {/* Progress Bar */}
            <View style={styles.progressBarContainer}>
              <View
                style={[
                  styles.progressBar,
                  { width: `${timerData.progress * 100}%`, backgroundColor: getProgressColor() }
                ]}
              />
            </View>

            {/* Remaining / Total */}
            <Text style={styles.subtitle}>{isPaused ? 'PAUSED' : `${remainingSeconds}s / ${totalSeconds}s`}</Text>

            {/* Active Schedules List */}
            {!isPaused && schedules && schedules.length > 0 && (
              <View style={styles.scheduleSection}>
                <Text style={styles.sectionTitle}>Active Schedules ({schedules.length})</Text>
                {schedules.map((s) => {
                  const firesIn = Math.max(0, s.scheduledFor - now)

                  return (
                    <View key={s._id} style={styles.scheduleItem}>
                      <Text style={styles.scheduleType}>{s.scheduleType}</Text>
                      <Text style={styles.scheduleTime}>{(firesIn / 1000).toFixed(1)}s</Text>
                    </View>
                  )
                })}
              </View>
            )}

            {/* Debug Info Section */}
            <View style={styles.debugInfo}>
              <View style={styles.debugRow}>
                <Text style={styles.debugLabel}>Player:</Text>
                <Text style={styles.debugValue}>{playerType}</Text>
              </View>
              <View style={styles.debugRow}>
                <Text style={styles.debugLabel}>Type:</Text>
                <Text style={styles.debugValue}>{timerData.scheduleType}</Text>
              </View>
              <View style={styles.debugRow}>
                <Text style={styles.debugLabel}>State:</Text>
                <Text style={styles.debugValue}>{isPaused ? 'Paused' : 'Active'}</Text>
              </View>
              {showBotDualTimer && timerData.actualBotDelay && (
                <View style={styles.debugRow}>
                  <Text style={styles.debugLabel}>Bot Delay:</Text>
                  <Text style={styles.debugValue}>{timerData.actualBotDelay.toFixed(0)}ms</Text>
                </View>
              )}
            </View>

            {/* Pause/Resume Button */}
            <Pressable
              onPress={() => {
                isPaused ? resumeGame({ gameId }) : pauseGame({ gameId })
              }}
              style={({ pressed }) => [styles.button, isPaused && styles.activeButton, pressed && { opacity: 0.7 }]}
            >
              <Text style={[styles.buttonText, isPaused && styles.activeButtonText]}>
                {isPaused ? 'Resume' : 'Pause'}
              </Text>
            </Pressable>
          </View>
        )}
      </View>
    </Draggable>
  )
}

const styles = StyleSheet.create({
  collapseIcon: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.7)'
  },
  closeIcon: {
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.7)',
    fontWeight: '600'
  },
  content: {
    paddingTop: 8
  },
  draggableContainer: {
    position: 'absolute',
    top: 100,
    right: 10,
    zIndex: 100
  },
  container: {
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    padding: 8,
    borderRadius: 12,
    minWidth: 260,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 6,
    elevation: 8,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)'
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8
  },
  title: {
    color: 'white',
    fontSize: 13,
    fontWeight: '600',
    textAlign: 'center'
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4
  },
  activeDot: {
    backgroundColor: '#4CAF50'
  },
  pausedDot: {
    backgroundColor: '#FFA726'
  },
  waitingDot: {
    backgroundColor: '#9E9E9E'
  },
  waitingText: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: 14,
    textAlign: 'center',
    marginVertical: 20
  },
  dualTimerContainer: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 8
  },
  timerColumn: {
    flex: 1,
    alignItems: 'center'
  },
  timerLabel: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 10,
    fontWeight: '600',
    marginBottom: 4
  },
  timer: {
    color: '#4CAF50',
    fontSize: 36,
    fontWeight: 'bold',
    marginBottom: 8,
    textAlign: 'center',
    letterSpacing: 2
  },
  timerSmall: {
    fontSize: 28
  },
  timerUrgent: {
    color: '#FFA726'
  },
  timerCritical: {
    color: '#EF5350'
  },
  timerPaused: {
    color: '#9E9E9E'
  },
  progressBarContainer: {
    height: 6,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 3,
    overflow: 'hidden',
    marginBottom: 8
  },
  progressBar: {
    height: '100%',
    borderRadius: 3
  },
  subtitle: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 11,
    marginBottom: 12,
    textAlign: 'center'
  },
  debugInfo: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 6,
    padding: 8,
    marginBottom: 12,
    gap: 4
  },
  debugRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  debugLabel: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 10,
    fontWeight: '500'
  },
  debugValue: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: 10,
    fontWeight: '600',
    fontFamily: 'monospace'
  },
  scheduleSection: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 6,
    padding: 8,
    marginBottom: 12,
    gap: 4
  },
  sectionTitle: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: 11,
    fontWeight: '600',
    marginBottom: 4
  },
  scheduleItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 4
  },
  scheduleType: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 10,
    fontWeight: '500'
  },
  scheduleTime: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: 10,
    fontWeight: '600',
    fontFamily: 'monospace'
  },
  button: {
    padding: 10,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 0,
    backgroundColor: CREAM
  },
  activeButton: {
    backgroundColor: BLACK
  },
  buttonText: {
    color: BLACK,
    fontSize: 13,
    fontWeight: '600'
  },
  activeButtonText: {
    color: CREAM
  }
})
